import React, { useState } from 'react';
import PropTypes from 'prop-types';

/**
 * NoteViewer - Display visit notes with multiple format options
 * Provider-facing component with HTML, Plain Text, and RTF views
 * Mobile-responsive design with card layout using Bootstrap
 */
const NoteViewer = ({
  note,
  mode = 'compact',
  formats = null,
  onConvertFormat = null,
  convertStatus = 'idle',
  convertError = null,
  // onEdit = null, // Removed - editing functionality needs to be fixed later (AR 02/05/26)
  onSendToCore = null,
  isSending = false
}) => {
  const [isExpanded, setIsExpanded] = useState(mode === 'full');
  const [activeTab, setActiveTab] = useState('html');
  const [showRawHtml, setShowRawHtml] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const copyToClipboard = async (text, fieldId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldId);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      alert('Failed to copy to clipboard');
    }
  };

  const CopyButton = ({ text, fieldId }) => (
    <button
      className="btn btn-sm btn-outline-secondary"
      onClick={() => copyToClipboard(text, fieldId)}
      title="Copy to clipboard"
      disabled={!text}
    >
      <i className={`fas ${copiedField === fieldId ? 'fa-check text-success' : 'fa-copy'} me-1`}></i>
      {copiedField === fieldId ? 'Copied!' : 'Copy'}
    </button>
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Extract text from note content (handles both string and object formats)
  const extractTextFromContent = (content) => {
    if (!content) return '';

    if (typeof content === 'string') {
      return content;
    }

    if (typeof content === 'object') {
      const parts = [];
      for (const [key, value] of Object.entries(content)) {
        if (value) {
          const sectionName = key
            .replace(/([A-Z])/g, ' $1')
            .replace(/_/g, ' ')
            .trim()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

          if (typeof value === 'object' && value.content) {
            parts.push(`${sectionName}:\n${value.content}`);
          } else if (typeof value === 'string') {
            parts.push(`${sectionName}:\n${value}`);
          }
        }
      }
      return parts.join('\n\n');
    }
    return '';
  };

  // Prefer pre-formatted plain_text from formats, fallback to extracting from content
  const plainText = formats?.plain_text || extractTextFromContent(note.content);
  const toggleExpand = () => mode === 'compact' && setIsExpanded(!isExpanded);
  const showTabs = mode === 'full' || (mode === 'compact' && isExpanded);

  const tabs = [
    { key: 'plain_text', label: 'Plain Text' },
    { key: 'html', label: 'Beautified (HTML)' },
    { key: 'rtf', label: 'Custom (RTF)' }
  ];

  return (
    <div className={`card shadow-sm ${mode === 'compact' ? 'mb-3' : ''}`}>
      <div className="card-body">
        {/* Header */}
        <div className="mb-2">
          <div className="d-flex justify-content-between align-items-start gap-3">
            <h6 className="text-primary fw-semibold mb-0 flex-grow-1">
              {note.template?.name || 'Visit Note'}
              {note.is_latest && <span className="badge bg-success mx-2">Latest</span>}
              <span className="badge bg-secondary">v{note.version || '1'}</span>
            </h6>
            {/* Action buttons */}
            <div className="d-flex gap-2">
              {/* Edit button hidden - editing functionality needs to be fixed later - AR 02/05/26 */}
              {/* {onEdit && (
                <button
                  className="btn btn-sm btn-outline-success"
                  onClick={() => onEdit(note)}
                  title="Edit note"
                >
                  <i className="fas fa-edit me-1"></i>Edit
                </button>
              )} */}
              {onSendToCore && (
                <button
                  className="btn btn btn-outline-primary"
                  onClick={() => onSendToCore(note)}
                  title="Send to Core Server"
                  disabled={isSending}
                >
                  <i className={`fas fa-rocket me-1 ${isSending ? 'rocket-launch' : ''}`}></i>
                  <span className="d-none d-sm-inline">{isSending ? 'Sending' : 'Send'}</span>
                </button>
              )}
              {mode === 'compact' && (
                <button
                  className="btn btn btn-outline-secondary"
                  onClick={toggleExpand}
                  title={isExpanded ? 'Collapse' : 'Expand'}
                >
                  <i className={`fas fa-${isExpanded ? 'eye-slash' : 'eye'} me-1`}></i>
                  <span className="d-none d-sm-inline">{isExpanded ? 'Hide' : 'View'}</span>
                </button>
              )}
            </div>
          </div>
          
            {!isExpanded && (
              <div className="d-flex gap-3 small text-muted mb-3">
              <span><i className="far fa-clock me-1"></i>{formatDate(note.created_at)}</span>
              {plainText && (
                <span><i className="fas fa-align-left me-1"></i>{plainText.trim().split(/\s+/).length} words</span>
              )}
            </div>
            )}
          
          {/* Compact mode preview */}
          {mode === 'compact' && !isExpanded && (
            <div className="text-muted small" style={{
              maxHeight: '3em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical'
            }}>
              {plainText.substring(0, 200)}...
            </div>
          )}
        </div>

      {/* Expanded content */}
      {isExpanded && (
        <>
          {/* Error alert */}
          {convertStatus === 'error' && convertError && (
            <div className="alert alert-danger alert-dismissible fade show py-2 mb-3" role="alert">
              <i className="fas fa-exclamation-circle me-1"></i>
              {convertError.error || 'Format conversion failed'}
            </div>
          )}

          {/* Tabs */}
          {showTabs && (
            <ul className="nav nav-tabs small mb-3">
              {tabs.map(tab => (
                <li className="nav-item" key={tab.key}>
                  <button
                    className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    <i className={`fas ${tab.icon} me-1`}></i>
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Tab content */}
          <div className="tab-content" style={{ maxHeight: mode === 'full' ? '600px' : '400px', overflowY: 'auto' }}>

            {/* Plain Text Tab */}
            {activeTab === 'plain_text' && (
              <div>
                <div className="d-flex justify-content-end mb-2">
                  {onConvertFormat && !formats?.plain_text && (
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={() => onConvertFormat('plain_text')}
                      disabled={convertStatus === 'loading'}
                    >
                      {convertStatus === 'loading' ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-sync-alt me-1"></i>
                          Generate Plain Text
                        </>
                      )}
                    </button>
                  )}
                  <CopyButton text={formats?.plain_text || plainText} fieldId={`plain-${note.id}`} />
                </div>
                <div
                  className="border rounded p-3 bg-light"
                  style={{ whiteSpace: 'pre-wrap', fontSize: '14px', lineHeight: '1.6' }}
                >
                  {formats?.plain_text || plainText}
                </div>
              </div>
            )}

              {/* HTML Tab */}
              {activeTab === 'html' && (
                <div style={{ minHeight: '200px' }}>
                  {formats?.html ? (
                    <>
                      <div className="d-flex justify-content-end gap-2 mb-2">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setShowRawHtml(!showRawHtml)}
                        >
                          <i className={`fas ${showRawHtml ? 'fa-eye' : 'fa-code'} me-1`}></i>
                          {showRawHtml ? 'Formatted' : 'Raw'}
                        </button>
                        <CopyButton text={formats.html} fieldId={`html-${note.id}`} />
                      </div>
                      {showRawHtml ? (
                        <pre className="bg-dark text-light rounded p-3 mb-0" style={{ maxHeight: '350px', overflowX: 'auto', fontSize: '13px', lineHeight: '1.5' }}>
                          <code>{formats.html}</code>
                        </pre>
                      ) : (
                        <iframe
                          title="Rendered HTML Note"
                          className="w-100 border rounded bg-white"
                          style={{ height: '350px' }}
                          srcDoc={formats.html}
                        />
                      )}
                    </>
                  ) : (
                    <div className="text-center py-5 bg-light border border-dashed rounded">
                      <div className="bg-secondary bg-opacity-25 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '64px', height: '64px' }}>
                        <i className="fas fa-code text-secondary fs-4"></i>
                      </div>
                      <p className="fw-semibold text-dark mb-1">HTML format not available</p>
                      <p className="text-muted small mb-3">Please generate the beautified version</p>
                      {onConvertFormat && (
                        <button
                          className="btn btn-primary"
                          onClick={() => onConvertFormat('html')}
                          disabled={convertStatus === 'loading'}
                        >
                          {convertStatus === 'loading' ? (
                            <><span className="spinner-border spinner-border-sm me-2" />Generating...</>
                          ) : (
                            <><i className="fas fa-sync-alt me-2"></i>Generate HTML</>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* RTF Tab */}
              {activeTab === 'rtf' && (
                <div style={{ minHeight: '200px' }}>
                  {formats?.rtf ? (
                    <>
                      <div className="d-flex justify-content-end gap-2 mb-2">
                        <CopyButton text={formats.rtf} fieldId={`rtf-${note.id}`} />
                      </div>
                      <pre className="bg-dark text-light rounded p-3 mb-0" style={{ maxHeight: '350px', overflowX: 'auto', fontSize: '13px', lineHeight: '1.5' }}>
                        {formats.rtf}
                      </pre>
                    </>
                  ) : (
                    <div className="text-center py-5 bg-light border border-dashed rounded">
                      <div className="bg-secondary bg-opacity-25 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '64px', height: '64px' }}>
                        <i className="fas fa-file-alt text-secondary fs-4"></i>
                      </div>
                      <p className="fw-semibold text-dark mb-1">RTF format not available</p>
                      <p className="text-muted small mb-3">Please generate the custom version</p>
                      {onConvertFormat && (
                        <button
                          className="btn btn-primary"
                          onClick={() => onConvertFormat('rtf')}
                          disabled={convertStatus === 'loading'}
                        >
                          {convertStatus === 'loading' ? (
                            <><span className="spinner-border spinner-border-sm me-2" />Generating...</>
                          ) : (
                            <><i className="fas fa-sync-alt me-2"></i>Generate RTF</>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

          {/* Metadata footer */}
          <div className="d-flex flex-wrap gap-3 pt-3 mt-3 text-muted small">
            <span>
              <i className="far fa-clock me-1"></i>
              Created: {formatDate(note.created_at)}
            </span>
          </div>
        </>
      )}
      </div>
    </div>
  );
};

NoteViewer.propTypes = {
  note: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    content: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    template: PropTypes.shape({
      name: PropTypes.string,
      description: PropTypes.string
    }),
    created_at: PropTypes.string,
    version: PropTypes.number,
    is_latest: PropTypes.bool
  }).isRequired,
  mode: PropTypes.oneOf(['compact', 'full']),
  formats: PropTypes.shape({
    html: PropTypes.string,
    plain_text: PropTypes.string,
    rtf: PropTypes.string
  }),
  onConvertFormat: PropTypes.func,
  convertStatus: PropTypes.oneOf(['idle', 'loading', 'error']),
  convertError: PropTypes.shape({
    error: PropTypes.string
  }),
  // onEdit: PropTypes.func, // Removed - editing functionality needs to be fixed
  onSendToCore: PropTypes.func,
  isSending: PropTypes.bool
};

export default NoteViewer;
